import {
  ExpandLess,
  ExpandMore,
  Refresh,
  Search as SearchIcon
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { KeyboardEvent, SyntheticEvent, useEffect, useState } from "react";

import { useDistinctProtocols } from "../data/hooks/useDistinctProtocols";
import { useDistinctUsers } from "../data/hooks/useDistinctUsers";
import { useSelectedGroups } from "../features/auth/hooks/useSelectedGroups";
import { APP_BAR_HEIGHT } from "../features/layout/LayoutContext";
import { FilterNames } from "../types/filter";
import { logError } from "../utils/error";
import { CONVERSATION_LENS } from "../utils/terminology";
import { TIME_RANGE_LABELS, TIME_RANGES, TimeRange } from "../utils/timerange";
import CustomDateRangePicker from "./CustomDateRangePicker";
import GroupsDisplay from "./GroupsDisplay";
import TimezoneDisplay from "./TimezoneDisplay";

export interface FilterBarProps {
  title?: string;
  subTitle?: string;
  timeRange: TimeRange;
  sentiment: string;
  relevance: string;
  risk: string;
  protocol: string;
  user: string;
  processDecision: string;
  callType: string;
  searchTerm?: string;
  scoreRange?: [number, number];
  totalConversations?: number;
  onFilterChange: (filterName: FilterNames, value: string) => void;
  onScoreRangeChange?: (value: [number, number]) => void;
  onRefresh?: () => void;
  showFilters?: {
    timeRange?: boolean;
    sentiment?: boolean;
    relevance?: boolean;
    risk?: boolean;
    protocol?: boolean;
    user?: boolean;
    processDecision?: boolean;
    callType?: boolean;
    scoreRange?: boolean;
    search?: boolean;
  };
  requiredFilters?: {
    timeRange?: boolean;
    protocol?: boolean;
    user?: boolean;
  };
  onExpandedChange?: (expanded: boolean) => void;
  sticky?: boolean;
  customOptions?: {
    users?: string[];
    protocols?: string[];
  };
}

const SCROLL_THRESHOLD = 50;

const FilterBar: React.FC<FilterBarProps> = (props) => {
  const {
    showFilters,
    requiredFilters,
    timeRange,
    sentiment,
    relevance,
    risk,
    protocol,
    user,
    processDecision,
    callType,
    searchTerm,
    scoreRange,
    title,
    subTitle,
    onFilterChange,
    onScoreRangeChange,
    onRefresh,
    onExpandedChange,
    sticky = false,
    customOptions
  } = props;

  const [isExpanded, setIsExpanded] = useState(true);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");
  const [isCustomDateDialogOpen, setIsCustomDateDialogOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Helper function to parse and validate date parts
  const parseCustomDateParts = (parts: string[]) => {
    try {
      const start = parseISO(parts[1]);
      const end = parseISO(parts[2]);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.warn(`Invalid date range in timeRange: ${parts[1]} to ${parts[2]}`);
        return { startDate: null, endDate: null };
      }

      return { startDate: start, endDate: end };
    } catch (error) {
      logError(`Failed to parse custom date range (parts: ${JSON.stringify(parts)})`, error);
      return { startDate: null, endDate: null };
    }
  };

  // Parse custom date range from timeRange prop if it's in custom format
  const getCurrentCustomDateRange = () => {
    if (!timeRange || !timeRange.startsWith("custom:")) {
      return { startDate: null, endDate: null };
    }

    const parts = timeRange.split(":");
    if (parts.length !== 3) {
      return { startDate: null, endDate: null };
    }

    return parseCustomDateParts(parts);
  };

  // Add local state for visual slider position
  const [localScoreRange, setLocalScoreRange] = useState<[number, number]>(
    scoreRange ?? [0, 5]
  );
  const { permittedEmails } = useSelectedGroups();

  // Use React Query hooks for protocols and users (only if customOptions not provided)
  const { data: fetchedProtocols = [], isLoading: isLoadingFetchedProtocols } = useDistinctProtocols({
    timeRange,
    permittedEmails
  });
  const { data: fetchedUsers = [], isLoading: isLoadingFetchedUsers } = useDistinctUsers({
    timeRange,
    permittedEmails
  });

  // Use custom options if provided, otherwise use fetched data
  const protocols = customOptions?.protocols ?? fetchedProtocols;
  const users = customOptions?.users ?? fetchedUsers;
  const isLoadingProtocols = !customOptions?.protocols && isLoadingFetchedProtocols;
  const isLoadingUsers = !customOptions?.users && isLoadingFetchedUsers;

  const showFilter = {
    timeRange: showFilters?.timeRange !== false, // Default to true
    sentiment: showFilters?.sentiment === true,
    relevance: showFilters?.relevance === true,
    risk: showFilters?.risk === true,
    protocol: showFilters?.protocol !== false, // Default to true
    user: showFilters?.user !== false, // Default to true
    processDecision: showFilters?.processDecision === true,
    callType: showFilters?.callType === true,
    scoreRange: showFilters?.scoreRange === true,
    search: showFilters?.search === true // Default to false
  };

  // Validate static dropdown values on component mount and when they change
  useEffect(() => {
    // Check call type (which has fixed options)
    if (callType !== "all" && callType !== "external" && callType !== "internal") {
      onFilterChange("callType", "all");
    }

    // Check sentiment
    if (sentiment !== "all" && sentiment !== "positive" &&
        sentiment !== "neutral" && sentiment !== "negative") {
      onFilterChange("sentiment", "all");
    }

    // Check relevance
    if (relevance !== "all" && relevance !== "high" &&
        relevance !== "medium" && relevance !== "low") {
      onFilterChange("relevance", "all");
    }

    // Check risk
    if (risk !== "all" && risk !== "high" &&
        risk !== "medium" && risk !== "low") {
      onFilterChange("risk", "all");
    }

    // Check process decision
    if (processDecision !== "all" && processDecision !== "DO_PROCESS" &&
        processDecision !== "DO_NOT_PROCESS") {
      onFilterChange("processDecision", "all");
    }

    // Check time range
    // Also allow custom date ranges in format custom:YYYY-MM-DD:YYYY-MM-DD
    const isCustomRange = timeRange && timeRange.startsWith("custom:");
    if (timeRange && !TIME_RANGES.includes(timeRange) && !isCustomRange) {
      onFilterChange("timeRange", "last7Days");
    }
  }, [callType, sentiment, relevance, risk, processDecision, timeRange, onFilterChange]);

  // Update local search term when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm || "");
  }, [searchTerm]);

  // Update local state when props change
  useEffect(() => {
    setLocalScoreRange(scoreRange ?? [0, 5]);
  }, [scoreRange]);

  useEffect(() => {
    if (!sticky) {
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > SCROLL_THRESHOLD);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sticky]);

  const handleChange = (event: SelectChangeEvent, filterName: FilterNames) => {
    onFilterChange(filterName, event.target.value);
  };

  const handleCustomDateRangeApply = (startDate: Date, endDate: Date) => {
    // Validate dates before formatting
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("Invalid date range provided");
      setIsCustomDateDialogOpen(false);
      return;
    }

    // Create a custom time range string with the date range
    const customTimeRange = `custom:${format(startDate, "yyyy-MM-dd")}:${format(endDate, "yyyy-MM-dd")}`;
    onFilterChange("timeRange", customTimeRange);
    setIsCustomDateDialogOpen(false);
  };

  const handleCustomDateRangeClear = () => {
    // Clear the time range filter completely
    onFilterChange("timeRange", "allTime");
    setIsCustomDateDialogOpen(false);
  };

  // Handle visual updates during dragging (no filter changes)
  const handleScoreRangeDrag = (_event: Event | SyntheticEvent, value: number | number[]) => {
    if (Array.isArray(value)) {
      setLocalScoreRange(value as [number, number]);
    }
  };

  // Handle actual filter update only when mouse is released
  const handleScoreRangeCommit = (_event: Event | SyntheticEvent, value: number | number[]) => {
    if (onScoreRangeChange && Array.isArray(value)) {
      onScoreRangeChange(value as [number, number]);
    }
  };

  // Create marks for the slider
  const scoreMarks = [
    { value: 0, label: "0" },
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" }
  ];

  // Function to get display name for a filter
  const getFilterDisplayName = (filterName: string) => {
    const filterDisplayNames: Record<string, string> = {
      timeRange: "Time",
      sentiment: "Sentiment",
      relevance: "Relevance",
      risk: "Risk",
      protocol: CONVERSATION_LENS,
      user: "User",
      processDecision: "Process",
      callType: "Call Type",
      scoreRange: "Score",
      search: "Search"
    };

    return filterDisplayNames[filterName] || filterName;
  };

  // Helper function to format custom date range display
  const formatCustomDateRange = () => {
    const customRange = getCurrentCustomDateRange();
    if (!customRange.startDate || !customRange.endDate) {
      return "Date Range";
    }

    try {
      return `${format(customRange.startDate, "MMM d")} - ${format(customRange.endDate, "MMM d")}`;
    } catch (error) {
      logError(`Failed to format date range for display (customRange: ${JSON.stringify(customRange)})`, error);
      return "Invalid Date Range";
    }
  };

  // Helper function to format date range for input field
  const formatDateRangeForInput = () => {
    // Check if it's a standard time range first (only the ones available in CustomDateRangePicker)
    if (timeRange && !timeRange.startsWith("custom:")) {
      const label = TIME_RANGE_LABELS[timeRange];
      if (label) {
        return label;
      }
    }

    // Handle custom date range
    const customRange = getCurrentCustomDateRange();
    if (!customRange.startDate || !customRange.endDate) {
      return "";
    }

    try {
      return `${format(customRange.startDate, "MMM d, yyyy")} - ${format(customRange.endDate, "MMM d, yyyy")}`;
    } catch (error) {
      logError(`Failed to format date range for input field (customRange: ${JSON.stringify(customRange)})`, error);
      return "";
    }
  };

  // Function to get display value for a filter
  const getFilterDisplayValue = (filterName: string, value: string | [number, number]) => {
    if (filterName === "scoreRange" && Array.isArray(value)) {
      return `${value[0]}-${value[1]}`;
    }

    if (value === "all") {
      return "All";
    }

    // Handle process decision special case
    if (filterName === "processDecision") {
      if (value === "DO_PROCESS") {
        return "Processed";
      }
      if (value === "DO_NOT_PROCESS") {
        return "Not Processed";
      }
    }

    // Handle call type display
    if (filterName === "callType") {
      if (value === "external") {
        return "External";
      }
      if (value === "internal") {
        return "Internal";
      }
    }

    // Handle time range display
    if (filterName === "timeRange") {
      if (value === "custom" || (typeof value === "string" && value.startsWith("custom:"))) {
        return formatCustomDateRange();
      }

      // Use the time range labels
      return TIME_RANGE_LABELS[value as string] || "Date Range";
    }

    return value;
  };

  // Function to determine if a filter is active (not default value)
  const isFilterActive = (filterName: string, value: string | [number, number]) => {
    if (filterName === "scoreRange" && Array.isArray(value)) {
      return value[0] !== 0 || value[1] !== 5; // Default is [0, 5]
    }

    if (filterName === "timeRange") {
      // Don't consider "last7Days" as default since user can select any time range
      return value !== "all" && value !== "";
    }

    return value !== "all" && value !== "";
  };

  // Get active filters to display as pills
  const getActiveFilters = () => {
    const activeFilters = [];

    if (showFilter.timeRange && isFilterActive("timeRange", timeRange)) {
      activeFilters.push({ name: "timeRange", value: timeRange, display: getFilterDisplayValue("timeRange", timeRange) });
    }

    if (showFilter.sentiment && isFilterActive("sentiment", sentiment)) {
      activeFilters.push({ name: "sentiment", value: sentiment, display: getFilterDisplayValue("sentiment", sentiment) });
    }

    if (showFilter.relevance && isFilterActive("relevance", relevance)) {
      activeFilters.push({ name: "relevance", value: relevance, display: getFilterDisplayValue("relevance", relevance) });
    }

    if (showFilter.risk && isFilterActive("risk", risk)) {
      activeFilters.push({ name: "risk", value: risk, display: getFilterDisplayValue("risk", risk) });
    }

    if (showFilter.protocol && isFilterActive("protocol", protocol)) {
      activeFilters.push({ name: "protocol", value: protocol, display: getFilterDisplayValue("protocol", protocol) });
    }

    if (showFilter.user && isFilterActive("user", user)) {
      activeFilters.push({ name: "user", value: user, display: getFilterDisplayValue("user", user) });
    }

    if (showFilter.processDecision && isFilterActive("processDecision", processDecision)) {
      activeFilters.push({ name: "processDecision", value: processDecision, display: getFilterDisplayValue("processDecision", processDecision) });
    }

    if (showFilter.callType && isFilterActive("callType", callType)) {
      activeFilters.push({ name: "callType", value: callType, display: getFilterDisplayValue("callType", callType) });
    }

    if (showFilter.scoreRange && scoreRange && (scoreRange[0] > 0 || scoreRange[1] < 5)) {
      activeFilters.push({ name: "scoreRange", value: scoreRange, display: getFilterDisplayValue("scoreRange", scoreRange) });
    }

    if (showFilter.search && searchTerm && searchTerm.trim() !== "") {
      activeFilters.push({ name: "search", value: searchTerm, display: searchTerm });
    }

    return activeFilters;
  };

  const handleRemoveFilter = (filterName: string) => {
    if (filterName === "scoreRange" && onScoreRangeChange) {
      onScoreRangeChange([0, 5]);
    } else if (filterName === "timeRange") {
      // Reset timeRange to default
      onFilterChange("timeRange", "allTime");
    } else if (filterName === "search") {
      // Empty string for search
      onFilterChange(filterName, "");
    } else {
      onFilterChange(filterName as FilterNames, "all");
    }
  };

  return (
    <Paper sx={{
      mb: 4,
      ...(sticky && {
        position: "sticky",
        top: APP_BAR_HEIGHT,
        zIndex: (theme) => theme.zIndex.appBar,
        transition: "box-shadow 0.2s ease-in-out",
        ...(isScrolled && {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          "&::after": {
            content: "\"\"",
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            height: "30px",
            background: "linear-gradient(to bottom, rgba(245, 245, 245, 1), rgba(245, 245, 245, 0.8), rgba(245, 245, 245, 0.1))",
            zIndex: -1,
            pointerEvents: "none"
          }
        })
      })
    }}>
      <Box sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        position: "relative"
      }}>
        <Box sx={{ flexDirection: "column" }}>
          <Typography variant="h6" sx={{ flex: "1 0 auto" }}>{title || "Filters"}</Typography>
          {subTitle && (
            <Typography variant="caption" color="text.secondary" sx={{ flex: "1 0 auto" }}>
              {subTitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", ml: 2, maxWidth: "calc(100% - 200px)" }}>
          {getActiveFilters().map((filter) => (
            <Chip
              key={filter.name}
              label={`${getFilterDisplayName(filter.name)}: ${filter.display}`}
              size="small"
              onDelete={() => handleRemoveFilter(filter.name)}
              sx={{
                backgroundColor: "#f0f9ff",
                borderRadius: "16px",
                fontWeight: "400",
                height: "24px",
                border: "1px solid #2196f3",
                "& .MuiChip-label": {
                  paddingLeft: 1,
                  paddingRight: 1
                },
                "& .MuiChip-deleteIcon": {
                  width: "16px",
                  height: "16px",
                  color: "#2196f3",
                  marginRight: "4px",
                  "&:hover": {
                    color: "#000000"
                  }
                }
              }}
            />
          ))}
        </Box>
        <Box sx={{ marginLeft: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <GroupsDisplay showLabel={true} />
          <TimezoneDisplay showTime={true} />
          <IconButton onClick={onRefresh ? onRefresh : () => {}}>
            <Refresh />
          </IconButton>
          <IconButton onClick={() => {
            const newExpanded = !isExpanded;
            setIsExpanded(newExpanded);
            if (onExpandedChange) {
              onExpandedChange(newExpanded);
            }
          }}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Grid container columns={20} spacing={2}>
            {showFilter.search && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Search</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter keyword, contact number..."
                  value={localSearchTerm || ""}
                  onChange={(e) => {
                    // Only update local state, not trigger filter change
                    setLocalSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onFilterChange("search", localSearchTerm);
                    }
                  }}
                  onBlur={() => {
                    onFilterChange("search", localSearchTerm);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => {
                            onFilterChange("search", localSearchTerm);
                          }}
                          size="small"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            )}

            {showFilter.timeRange && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Date Range</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Select date range"
                  value={formatDateRangeForInput()}
                  onClick={() => setIsCustomDateDialogOpen(true)}
                  InputProps={{
                    readOnly: true,
                    sx: { cursor: "pointer" }
                  }}
                />
              </Grid>
            )}

            {showFilter.sentiment && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Sentiment</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={sentiment}
                  onChange={(e) => handleChange(e, "sentiment")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="positive">Positive</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                  <MenuItem value="negative">Negative</MenuItem>
                </Select>
              </Grid>
            )}

            {showFilter.relevance && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Relevance</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={relevance}
                  onChange={(e) => handleChange(e, "relevance")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </Grid>
            )}

            {showFilter.risk && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Risk</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={risk}
                  onChange={(e) => handleChange(e, "risk")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </Grid>
            )}

            {showFilter.user && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>User</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={user}
                  onChange={(e) => {
                    handleChange(e, "user");
                  }}
                  disabled={isLoadingUsers}
                  startAdornment={isLoadingUsers ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  required={requiredFilters?.user}
                >
                  <MenuItem value="all">All</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user} value={user}>{user}</MenuItem>
                  ))}
                </Select>
              </Grid>
            )}

            {showFilter.protocol && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>{CONVERSATION_LENS}</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={protocol}
                  onChange={(e) => handleChange(e, "protocol")}
                  disabled={isLoadingProtocols}
                  startAdornment={isLoadingProtocols ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                >
                  <MenuItem value="all">All</MenuItem>
                  {protocols.map((protocol) => (
                    <MenuItem key={protocol} value={protocol}>{protocol}</MenuItem>
                  ))}
                </Select>
              </Grid>
            )}

            {showFilter.processDecision && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Process Decision</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={processDecision}
                  onChange={(e) => handleChange(e, "processDecision")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="DO_PROCESS">Processed</MenuItem>
                  <MenuItem value="DO_NOT_PROCESS">Not Processed</MenuItem>
                </Select>
              </Grid>
            )}

            {showFilter.callType && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>Call Type</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={callType}
                  onChange={(e) => handleChange(e, "callType")}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </Grid>
            )}

            {showFilter.scoreRange && (
              <Grid item xs={20} sm={8} md={4}>
                <Typography variant="caption" display="block" gutterBottom>
                  Score Range
                </Typography>
                <Stack spacing={2} direction="row" sx={{ mb: 1, px: 1 }} alignItems="center">
                  <Slider
                    value={localScoreRange}
                    onChange={handleScoreRangeDrag}
                    onChangeCommitted={handleScoreRangeCommit}
                    step={0.5}
                    marks={scoreMarks}
                    min={0}
                    max={5}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      </Collapse>

      {/* Custom Date Range Dialog */}
      <CustomDateRangePicker
        open={isCustomDateDialogOpen}
        onClose={() => setIsCustomDateDialogOpen(false)}
        onApply={handleCustomDateRangeApply}
        onApplyShortcut={(shortcut) => {
          onFilterChange("timeRange", shortcut);
          setIsCustomDateDialogOpen(false);
        }}
        onClear={handleCustomDateRangeClear}
        initialStartDate={getCurrentCustomDateRange().startDate}
        initialEndDate={getCurrentCustomDateRange().endDate}
        currentTimeRange={timeRange}
      />
    </Paper>
  );
};

export default FilterBar;
